import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Lightbox from "react-image-lightbox";
import { ErrorMessage, Form, Formik } from "formik";
import {
  useAddProductSectionMutation,
  useGetProductSectionMutation,
  useDeleteProductSectionMutation,
  useGetProductSectionPartsMutation,
  useUpdateProductSectionMutation,
} from "../../redux/reducer/features/Product/productSlice";
import {
  ProductSection,
  ProductSectionInitialValues,
  ProductSectionValidationSchema,
} from "../../models/features/Product/Product";
import { useGetPartsMutation } from "../../redux/reducer/features/Part/partSlice";
import { useNavigate } from "react-router-dom";
import PageLoader from "../app/PageLoader";
import { AppFilter } from "../../models/app/AppModels";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

interface ManageSectionProps {
  productId: any;
}
const ManageProductSection: React.FunctionComponent<ManageSectionProps> = ({
  productId,
}) => {
  const [paging, setPaging] = React.useState<AppFilter>({
    pageIndex: 0,
    pageSize: 50,
    totalRecords: 0,
    items: [],
    quickFilterValues: [],
  });
  var navigate = useNavigate();
  const [imageURL, setURL] = React.useState("");
  const [isOpen, setOpen] = React.useState(false);
  const [openForm, setOpenForm] = React.useState(false);
  const [openEditForm, setOpenEditForm] = React.useState(false);
  const [imageFile, setImageFile] = React.useState<File>();
  const [selectedRow, setSelectedRow] = React.useState<ProductSection>();
  const [partOption, setPartOption] = React.useState<any[]>([]);
  const [searchPart, setSearchPart] = React.useState("");
  const [selectedParts, setSelectedParts] = React.useState<any[]>([]);
  const [productSections, setProductSections] = React.useState<any[]>([]);

  const [partsDataMutation, partsDataMutationResult] = useGetPartsMutation();
  const [productSectionMutation, productSectionMutationResult] =
    useAddProductSectionMutation();
  const [updateProductSectionMutation, updateProductSectionMutationResult] =
    useUpdateProductSectionMutation();
  const [getProductSectionMutation, getProductSectionMutationResult] =
    useGetProductSectionMutation();
  const [deleteProductSectionMutation, deleteProductSectionMutationResult] =
    useDeleteProductSectionMutation();
  const [getSectionPartsMutation, getSectionPartsMutationResult] =
    useGetProductSectionPartsMutation();

  React.useEffect(() => {
    GetParts();
  }, [paging]);

  React.useEffect(() => {
    if (openForm || openEditForm) {
      setPaging({ ...paging, quickFilterValues: [searchPart] });
    }
  }, [searchPart]);

  const GetParts = () => {
    partsDataMutation({ paging: paging });
  };
  React.useEffect(() => {
    if (partsDataMutationResult.isSuccess && partsDataMutationResult.data) {
      setPartOption(partsDataMutationResult.data.entityList);
    }
  }, [partsDataMutationResult]);

  React.useEffect(() => {
    getProductSectionMutation({ productId: productId });
  }, []);

  const handleUploadClick = (e: any) => {
    var file = e.target.files[0];
    setImageFile(file);
  };
  const handleClose = () => {
    setOpenForm(false);
    setOpen(false);
    setOpenEditForm(false);
  };
  const handleDelete = (id: any) => {
    deleteProductSectionMutation({ data: { id: id } });
  };
  const handleOnSubmit = (values: ProductSection) => {
    if (imageFile) {
      const formData = new FormData();
      formData.append("imageFile", imageFile);
      formData.append("sectionNumber", values.sectionNumber);
      formData.append("title", values.title);
      formData.append("shortTitle", values.title);
      formData.append("isActive", "true");
      formData.append("productId", productId);
      var partsIds = selectedParts.map((item) => item?.id);
      formData.append("partIds", JSON.stringify(partsIds));
      productSectionMutation({ data: formData });
    }
  };
  const handleUpdateSection = (values: ProductSection) => {
    const formData = new FormData();
    if (imageFile && values.imgUrl === "") {
      formData.append("imageFile", imageFile);
    }
    formData.append("id", values.id);
    formData.append("imgUrl", selectedRow?.imgUrl ?? "");
    formData.append("sectionNumber", values.sectionNumber);
    formData.append("title", values.title);
    formData.append("shortTitle", values.shortTitle);
    formData.append("isActive", "true");
    var partsIds = selectedParts.map((item) => item?.id);
    formData.append("partIds", JSON.stringify(partsIds));
    updateProductSectionMutation({ data: formData });
  };

  React.useEffect(() => {
    if (
      productSectionMutationResult.isSuccess &&
      productSectionMutationResult.data
    ) {
      setProductSections([
        ...productSections,
        productSectionMutationResult.data.entity,
      ]);
      setOpenForm(false);
    }
  }, [productSectionMutationResult]);

  React.useEffect(() => {
    if (
      updateProductSectionMutationResult.isSuccess &&
      updateProductSectionMutationResult.data
    ) {
      let section = updateProductSectionMutationResult.data.entity;
      setProductSections(
        productSections.map((sec) =>
          sec.id === section?.id
            ? {
                ...sec,
                sectionNumber: section?.sectionNumber,
                title: section?.title,
                shortTitle: section?.shortTitle,
                imgUrl: section?.imgUrl,
              }
            : sec
        )
      );
      setOpenEditForm(false);
    }
  }, [updateProductSectionMutationResult]);

  React.useEffect(() => {
    if (
      deleteProductSectionMutationResult.isSuccess &&
      deleteProductSectionMutationResult.data?.status === 0
    ) {
      setProductSections(
        productSections.filter((item) => item.id !== selectedRow?.id)
      );
    }
  }, [deleteProductSectionMutationResult]);

  React.useEffect(() => {
    if (
      getSectionPartsMutationResult.isSuccess &&
      getSectionPartsMutationResult.data?.status === 0
    ) {
      setSelectedParts(getSectionPartsMutationResult?.data?.entityList);
      setOpenEditForm(true);
    }
  }, [getSectionPartsMutationResult]);

  React.useEffect(() => {
    if (
      getProductSectionMutationResult.isSuccess &&
      getProductSectionMutationResult.data
    ) {
      setProductSections(getProductSectionMutationResult.data.entityList);
    }
  }, [getProductSectionMutationResult]);
  return (
    <>
      <PageLoader
        isLoading={
          getProductSectionMutationResult.isLoading ||
          getSectionPartsMutationResult.isLoading
        }
      ></PageLoader>
      <Box sx={{ mb: 3, textAlign: "right" }}>
        <Button
          onClick={() => {
            setSelectedParts([]);
            setImageFile(undefined);
            setOpenForm(true);
          }}
          variant="contained"
          startIcon={<AddIcon />}
        >
          New Section
        </Button>
      </Box>
      <Card style={{ padding: "10px", margin: "10px" }}>
        <Table aria-label="customized table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Sr.No</TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="left">
                Section No.
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="left">
                Title
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="center">
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {productSections
              ? productSections.map((item, index) => (
                  <StyledTableRow>
                    <StyledTableCell sx={{ padding: "10px" }} scope="row">
                      {index + 1}
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={{ padding: "2px" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            width: "50px",
                            height: "60px",
                            backgroundColor: "white",
                            borderRadius: "0.25rem",
                            border: "1px solid #DEE2E6",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setOpen(true);
                            setURL(item.imgUrl);
                          }}
                        >
                          <img
                            style={{ width: "100%", height: "100%" }}
                            alt=""
                            src={`${process.env.REACT_APP_BLOB_STORAGE_URL}/product-catalog/ProductImage/${item.imgUrl}`}
                          ></img>
                        </div>

                        <Typography
                          sx={{ ml: 2, fontSize: "14px", fontWeight: "bold" }}
                        >
                          {item.sectionNumber}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell align="left">{item.title}</StyledTableCell>
                    <StyledTableCell align="center">
                      <IconButton
                        id="basic-button"
                        onClick={(e) => {
                          navigate("/ViewSection/" + item?.id);
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      |
                      <IconButton
                        id="basic-button"
                        onClick={(n) => {
                          setImageFile(undefined);
                          setSelectedRow(item);
                          getSectionPartsMutation({ sectionId: item?.id });
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      |
                      <IconButton
                        id="basic-button"
                        onClick={(n) => {
                          setSelectedRow(item);
                          handleDelete(item?.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              : ""}
          </TableBody>
        </Table>
      </Card>
      {isOpen && (
        <Lightbox
          mainSrc={`${process.env.REACT_APP_BLOB_STORAGE_URL}/product-catalog/ProductImage/${imageURL}`}
          onCloseRequest={handleClose}
        />
      )}
      <Dialog
        maxWidth={"lg"}
        open={openForm}
        onClose={() => {
          handleClose();
        }}
      >
        <DialogTitle alignSelf={"center"}>Add Product Section</DialogTitle>
        <DialogContent>
          <Formik
            initialValues={ProductSectionInitialValues}
            validationSchema={ProductSectionValidationSchema}
            onSubmit={(values) => {
              handleOnSubmit(values);
            }}
            enableReinitialize={true}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
            }) => (
              <>
                <Form>
                  <Grid
                    container
                    spacing={3}
                    justifyContent="space-between"
                    display="flex"
                    p={5}
                  >
                    <Grid item xs={12}>
                      <TextField
                        id="title"
                        name="title"
                        label="Title"
                        fullWidth
                        autoComplete="family-name"
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.title}
                      />
                      <ErrorMessage
                        className="error-msg"
                        component={"div"}
                        name="title"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        id="shortTitle"
                        name="shortTitle"
                        label="Short Title"
                        fullWidth
                        autoComplete="family-name"
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.shortTitle}
                      />
                      <ErrorMessage
                        className="error-msg"
                        component={"div"}
                        name="shortTitle"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        id="sectionNumber"
                        name="sectionNumber"
                        label="Section Number"
                        fullWidth
                        autoComplete="family-name"
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.sectionNumber}
                      />
                      <ErrorMessage
                        className="error-msg"
                        component={"div"}
                        name="sectionNumber"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        id="tags-standard"
                        disableClearable
                        getOptionLabel={(option: any) =>
                          option.title + " ," + option.partNumber
                        }
                        options={partOption}
                        style={{ padding: "0px", margin: "1px !important" }}
                        onBlur={(e) => {}}
                        clearOnBlur
                        onKeyDown={(event: any) => {
                          setSearchPart(event.target.value);
                        }}
                        onKeyUp={(event: any) => {
                          setSearchPart(event.target.value);
                        }}
                        onChange={(event, newValue) => {
                          setSelectedParts(newValue);
                        }}
                        value={selectedParts}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              color="primary"
                              size="medium"
                              label={option.title}
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            fullWidth
                            id="part"
                            name="part"
                            label="Add Parts"
                            type="text"
                            placeholder="Search by Part Number, Title, etc..."
                            inputProps={{
                              ...params.inputProps,
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <TextField
                        type="file"
                        id="imageFile"
                        name="imageFile"
                        label="Image"
                        fullWidth
                        autoComplete="family-name"
                        variant="standard"
                        onChange={handleUploadClick}
                        onBlur={handleBlur}
                      />
                      <ErrorMessage
                        className="error-msg"
                        component={"div"}
                        name="File"
                      />
                    </Grid>

                    <Grid item xs={12} mt={3}>
                      <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-end"
                      >
                        <Grid item>
                          <Button
                            color="error"
                            variant="contained"
                            onClick={() => {
                              handleClose();
                            }}
                          >
                            Cancel
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            disabled={productSectionMutationResult.isLoading}
                            variant="contained"
                            type="submit"
                            startIcon={
                              productSectionMutationResult.isLoading ? (
                                <CircularProgress
                                  size="1.2rem"
                                  color="inherit"
                                />
                              ) : (
                                ""
                              )
                            }
                          >
                            Save
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Form>
              </>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
      <Dialog
        maxWidth={"lg"}
        open={openEditForm}
        onClose={() => {
          handleClose();
        }}
      >
        <DialogTitle alignSelf={"center"}>Edit Product Section</DialogTitle>
        <DialogContent>
          <Formik
            initialValues={selectedRow ?? ProductSectionInitialValues}
            validationSchema={ProductSectionValidationSchema}
            onSubmit={(values) => {
              handleUpdateSection(values);
            }}
            enableReinitialize={true}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
            }) => (
              <>
                <Form>
                  <Grid
                    container
                    spacing={3}
                    justifyContent="space-between"
                    display="flex"
                    p={5}
                  >
                    <Grid item xs={12}>
                      <TextField
                        id="title"
                        name="title"
                        label="Title"
                        fullWidth
                        autoComplete="family-name"
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.title}
                      />
                      <ErrorMessage
                        className="error-msg"
                        component={"div"}
                        name="title"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        id="shortTitle"
                        name="shortTitle"
                        label="Short Title"
                        fullWidth
                        autoComplete="family-name"
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.shortTitle}
                      />
                      <ErrorMessage
                        className="error-msg"
                        component={"div"}
                        name="shortTitle"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        id="sectionNumber"
                        name="sectionNumber"
                        label="Section Number"
                        fullWidth
                        autoComplete="family-name"
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.sectionNumber}
                      />
                      <ErrorMessage
                        className="error-msg"
                        component={"div"}
                        name="sectionNumber"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        id="tags-standard"
                        disableClearable
                        getOptionLabel={(option: any) =>
                          option.title + " ," + option.partNumber
                        }
                        options={partOption}
                        style={{ padding: "0px", margin: "1px !important" }}
                        onBlur={(e) => {}}
                        clearOnBlur
                        onKeyDown={(event: any) => {
                          setSearchPart(event.target.value);
                        }}
                        onKeyUp={(event: any) => {
                          setSearchPart(event.target.value);
                        }}
                        onChange={(event, newValue) => {
                          setSelectedParts(newValue);
                        }}
                        value={selectedParts}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              color="primary"
                              size="medium"
                              label={option.title}
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            fullWidth
                            id="part"
                            name="part"
                            label="Add Parts"
                            type="text"
                            placeholder="Search by Part Number, Title, etc..."
                            inputProps={{
                              ...params.inputProps,
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} mt={2}>
                      <label htmlFor="ImgUrl">
                        <TextField
                          type="file"
                          sx={{ display: "none" }}
                          id="ImgUrl"
                          name="ImgUrl"
                          label="Image File"
                          fullWidth
                          disabled={values.imgUrl !== ""}
                          autoComplete="family-name"
                          variant="standard"
                          onChange={handleUploadClick}
                          onBlur={handleBlur}
                        />

                        <Button
                          size="small"
                          disabled={values.imgUrl !== ""}
                          variant="contained"
                          component="span"
                        >
                          Upload Image
                        </Button>

                        <ErrorMessage
                          className="error-msg"
                          component={"div"}
                          name="ImgUrl"
                        />
                      </label>
                      <br />
                      {imageFile?.name ? (
                        <div
                          style={{
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          <img
                            style={{
                              width: "150px",
                              height: "100px",
                              marginTop: "10px",
                              borderRadius: 5,
                            }}
                            alt="preview image"
                            src={URL.createObjectURL(imageFile)}
                          />
                          <IconButton
                            size="small"
                            style={{
                              position: "absolute",
                              top: 6,
                              right: -3,
                              backgroundColor: "white",
                            }}
                            onClick={() => {
                              setImageFile(undefined);
                            }}
                          >
                            <CloseIcon
                              sx={{
                                color: "rgb(56, 55, 110)",
                                fontSize: "13px",
                              }}
                            />
                          </IconButton>
                        </div>
                      ) : values.imgUrl ? (
                        ""
                      ) : (
                        <Typography>No File Selected</Typography>
                      )}
                      {values.imgUrl ? (
                        <div
                          style={{
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          <img
                            style={{
                              width: "150px",
                              height: "100px",
                              marginTop: "10px",
                              borderRadius: 5,
                            }}
                            alt="preview image"
                            src={`${process.env.REACT_APP_BLOB_STORAGE_URL}/product-catalog/ProductImage/${values.imgUrl}`}
                          />
                          <IconButton
                            size="small"
                            style={{
                              position: "absolute",
                              top: 6,
                              right: -3,
                              backgroundColor: "white",
                            }}
                            onClick={() => {
                              setFieldValue("imgUrl", "");
                              setImageFile(undefined);
                            }}
                          >
                            <CloseIcon
                              sx={{
                                color: "rgb(56, 55, 110)",
                                fontSize: "13px",
                              }}
                            />
                          </IconButton>
                        </div>
                      ) : (
                        ""
                      )}
                    </Grid>
                    <Grid item xs={12} mt={3}>
                      <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-end"
                      >
                        <Grid item>
                          <Button
                            color="error"
                            variant="contained"
                            onClick={() => {
                              handleClose();
                            }}
                          >
                            Cancel
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            disabled={
                              updateProductSectionMutationResult.isLoading
                            }
                            variant="contained"
                            type="submit"
                            startIcon={
                              updateProductSectionMutationResult.isLoading ? (
                                <CircularProgress
                                  size="1.2rem"
                                  color="inherit"
                                />
                              ) : (
                                ""
                              )
                            }
                          >
                            Update
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Form>
              </>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManageProductSection;
